import React, { useRef } from 'react';

import {
  ChakraFlex,
  ChakraText,
  EquipUITheme,
  InfiniteScroll,
} from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  ChatDividerType,
  MARK_READ_DURATION,
  MESSAGE_LIST_MARGIN,
} from '~/lib/constants/chat.constants';
import { ChatMessage } from '~/lib/types/chat';
import { getUnreadMessageInfo } from '~/lib/util/chat/chat.utils';

import ChatDivider from './ChatDivider';
import PatientMyMessageTile from './PatientMyMessageTile';
import NoMessagesState from './NoMessagesState';

const idPrefix = 'messages';
const idPostfix = 'list-panel';

const { captionBold } = EquipUITheme.typography;

interface PatientMyMessagesPanelProps {
  channel: GroupChannel;
  chatUserId: string;
  hasMoreMessages: boolean;
  id: string;
  isChannelReadOnly: boolean;
  isGroupChannel: boolean;
  isMessageSent: boolean;
  loadMoreMessages: (oldestMessage?: ChatMessage) => void;
  markMessagesAsRead: () => void;
  messages: ChatMessage[];
  onInputFocus: (inputRef: React.MutableRefObject<HTMLInputElement>) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  onRepliesExpanded: (message: ChatMessage, onSuccess?: () => void) => void;
  onSendMessage: (newMessage: ChatMessage) => void;
  patientId: string;
}

const PatientMyMessagesPanel = React.forwardRef<
  HTMLDivElement,
  PatientMyMessagesPanelProps
>(
  (
    {
      channel,
      chatUserId,
      hasMoreMessages,
      id,
      isChannelReadOnly,
      isGroupChannel,
      isMessageSent,
      loadMoreMessages,
      markMessagesAsRead,
      messages,
      onInputFocus,
      onMessageRemoved,
      onRepliesExpanded,
      onSendMessage,
      patientId,
    }: PatientMyMessagesPanelProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const containerId = `${idPrefix}-container__${id}__${idPostfix}`;
    const unreadMessagesInfo = getUnreadMessageInfo(messages);
    const messageRef = useRef<HTMLDivElement>(null);
    const firstUnreadMessageRef = useRef<HTMLDivElement>(null);
    const lastUnreadMessageRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { maudProviderChatMessagesPrototype: isMessageThreadingEnabled } =
      useFlags<LDFlags>();

    const oldestMessage = messages?.reduce((oldest, current) => {
      return current.createdAt < oldest.createdAt ? current : oldest;
    }, messages[0]);

    const getMessageProps = (message: ChatMessage) => ({
      channel,
      id: message?.messageId.toFixed(),
      isChannelReadOnly,
      isGroupChannel,
      isMessageSent,
      message,
      onInputFocus,
      onMessageRemoved,
      onRepliesExpanded,
      onSendMessage,
      patientId,
    });

    const unreadMessageNavigatorProps = {
      backgroundColor: 'secondary.50',
      borderRadius: '100px',
      color: 'white',
      cursor: 'pointer',
      left: '50%',
      padding: '4px 8px',
      transform: 'translateX(-50%)',
    };

    if (messages?.length === 0)
      return (
        <ChakraFlex
          direction="column"
          height="100%"
          id={containerId}
          justifyContent="end"
          ref={ref}
          width="100%"
        >
          <NoMessagesState
            id={idPrefix}
            isChannelReadonly={isChannelReadOnly}
          />
        </ChakraFlex>
      );

    if (messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'auto' });
      if (Math.abs(messageRef.current.scrollTop) < MESSAGE_LIST_MARGIN) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(markMessagesAsRead, MARK_READ_DURATION);
      }
      messageRef.current = null;
    }

    // This is not working as expected for the new feature.
    const onScroll = (e: MouseEvent) => {
      const { scrollTop } = e.target as HTMLDivElement;

      if (Math.abs(scrollTop) < MESSAGE_LIST_MARGIN) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(markMessagesAsRead, MARK_READ_DURATION);
      } else {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onScrollV2 = () => {};

    return (
      <>
        {unreadMessagesInfo.totalUnreadMessages > 0 && isGroupChannel && (
          <>
            <ChakraText
              position="absolute"
              top="16px"
              {...captionBold}
              {...unreadMessageNavigatorProps}
              onClick={() => {
                if (lastUnreadMessageRef.current) {
                  lastUnreadMessageRef.current.scrollIntoView({
                    behavior: 'smooth',
                  });
                } else if (firstUnreadMessageRef.current) {
                  firstUnreadMessageRef.current.scrollIntoView({
                    behavior: 'smooth',
                  });
                }
              }}
            >
              Unread message above
            </ChakraText>
            <ChakraText
              bottom="16px"
              position="absolute"
              {...captionBold}
              {...unreadMessageNavigatorProps}
              onClick={() => {
                if (firstUnreadMessageRef.current) {
                  firstUnreadMessageRef.current.scrollIntoView({
                    behavior: 'smooth',
                  });
                } else if (lastUnreadMessageRef.current) {
                  lastUnreadMessageRef.current.scrollIntoView({
                    behavior: 'smooth',
                  });
                }
              }}
            >
              Unread message below
            </ChakraText>
          </>
        )}
        <ChakraFlex
          direction="column-reverse"
          height="100%"
          id={containerId}
          overflowY="auto"
          ref={ref}
          width="100%"
        >
          <InfiniteScroll
            dataLength={(messages ?? []).length}
            fetchData={() => loadMoreMessages(oldestMessage)}
            hasMore={hasMoreMessages}
            hideCount
            id={`${idPrefix}-wrapper__${id}__${idPostfix}`}
            inverse={!!messages}
            onScroll={isMessageThreadingEnabled ? onScrollV2 : onScroll}
            scrollableTarget={containerId}
            style={{ overflowX: 'hidden' }}
            totalCount={(messages ?? []).length * 100}
            width="100%"
          >
            {(messages ?? []).map((message, index) => {
              return (
                <React.Fragment key={message.id}>
                  {unreadMessagesInfo.lastUnreadMessageId ===
                    message?.messageId && (
                    <PatientMyMessageTile
                      {...getMessageProps(message)}
                      chatUserId={chatUserId}
                      key={message?.messageId.toFixed()}
                      ref={messageRef}
                      unreadRef={lastUnreadMessageRef}
                    />
                  )}

                  {unreadMessagesInfo.totalUnreadMessages > 1 &&
                    unreadMessagesInfo.firstUnreadMessageId ===
                      message?.messageId && (
                      <PatientMyMessageTile
                        {...getMessageProps(message)}
                        chatUserId={chatUserId}
                        key={message?.messageId.toFixed()}
                        unreadRef={firstUnreadMessageRef}
                      />
                    )}

                  {unreadMessagesInfo.firstUnreadMessageId !==
                    message?.messageId &&
                    unreadMessagesInfo.lastUnreadMessageId !==
                      message?.messageId && (
                      <PatientMyMessageTile
                        {...getMessageProps(message)}
                        chatUserId={chatUserId}
                        key={message?.messageId.toFixed()}
                      />
                    )}

                  {(index === messages.length - 1 ||
                    message.relativeDate !==
                      messages[index + 1]?.relativeDate) && (
                    <ChatDivider
                      relativeDate={message.relativeDate}
                      text={message.relativeDate}
                      type={ChatDividerType.DATE}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </InfiniteScroll>
        </ChakraFlex>
      </>
    );
  },
);

PatientMyMessagesPanel.displayName = 'PatientMyMessagesPanel';

export default PatientMyMessagesPanel;
